import { useEffect, useRef, useState } from 'react'
import styled from 'styled-components'
import DefaultLayout from '../components/molecules/common/DefaultLayout'
import TranscriptSection from '../components/molecules/TranscriptSection'
import RecordingMetaHeader from '../components/molecules/RecordingMetaHeader'
import { useNavigate, useParams } from 'react-router-dom'
import { createConversationWord, deleteConversation, getConversation, updateTitle } from '../api/conversation'
import { useModal } from '../hooks/useModal'
import { useDropdown } from '../hooks/useDropdown'
import { useClickOutside } from '../hooks/useClickOutside'
import ConversationDetailDropdown from '../components/molecules/ConversationDetailDropdown'
import EditTitleModal from '../components/molecules/EditTitleModal'
import DeleteModal from '../components/molecules/common/DeleteModal'
import { formatDuration } from '../utils/formatDuration'
import { formatUtcToLocalTime } from '../utils/fomratUtcToLocalTime'
import AudioPlayer from '../components/molecules/AudioPlayer'
import { useBack } from '../hooks/useBack'
import LoadingOverlay from '../components/molecules/common/LoadingOverlay'
import { logEvent } from '../utils/eventLogger'

const AudioPlayerWrapper = styled.div`
  position: fixed;
  bottom: 0;
  left: 50%;
  transform: translateX(-50%);
  width: 100%;
  max-width: 600px; /* Wrapper 최대 폭과 맞춤 */
`
const ConversationTranscript = () => {
  const [isSubmitting, setIsSubmitting] = useState(false); // api 호출 상태
  const [showLoading, setShowLoading] = useState(false); // 화면에 로딩 오버레이 렌더링 여부 

  const [title, setTitle] = useState("")

  const handleChangeTitle = async(newTitle) => {
     // 단어 등록 전 -> 서버 호출 X
    if (transcriptData?.mappingYn === false) {
      setTitle(newTitle);
      return;
    }

    // 단어 등록 후 → 서버 호출
    try {
      const payload = {title: newTitle}
      const res = await updateTitle(id, payload);
      setTitle(newTitle);
      // console.log(res)
    } catch (err) {
      console.error(err);
      alert("제목 수정을 실패했어요. 다시 시도해주세요.");
    }
  }

  const [transcriptData, setTranscriptData] = useState(null);
  const [newMeanings, setNewMeanings] = useState({})
  
  const handleMeaningChange = (key, meaning, rank) => {
    setNewMeanings(prev => ({
      ...prev,
      [key]: {
        ...prev[key],
        selectedMeaning: meaning,
        selectedRank: rank
      },
    }));
    // console.log(newMeanings)
  };

  const isEditable = transcriptData?.mappingYn === false;

  const { id } = useParams();

  useEffect(() => {
    const fetchConversation = async() => {
      try {
        const data = await getConversation(id);
        // console.log(data)
        setTranscriptData(data);
        setTitle(data.title);
      } catch (err) {
        console.error(err)
        if (err.response?.status === 400) {
          alert("접근 권한이 없습니다.");
          navigate('/conversations', { replace: true });
        }
      }
    };
    fetchConversation();
  }, [id])
  
  const createMappingPayload = () => {
    return {
      title: title,
      recordedAt: transcriptData.recordedAt,
      s3AudioUrl: transcriptData.s3AudioUrl,
      totalTime: transcriptData.totalTime,
      mappingYn: transcriptData.mappingYn,

      segments: transcriptData.segments.map((segment) => ({
        ...segment,
        content: segment.content.map((word) => {
          const key = `${segment.sequence}-${word.wordSerialNum}`;
          const updated = newMeanings[key];

          // 화자가 아동이 아닌 경우
          if (segment.speaker === false) {
            return {
              ...word,
              selectedNum: null
            }
          }

          // 유저가 수동으로 변경한 경우
          if (updated) {
            return {
              ...word,
              meaning: updated.selectedMeaning,
              selectedNum: updated.selectedRank,
            };
          }

          // 유저가 변경하지 않은 경우 중, rate가 0.5보다 낮은 경우(추천 의미단어가 노란색)
          const isLowConfidence = word.rate && word.rate.length > 0 && word.rate[0].rate < 0.5;
          if (isLowConfidence) {
            return {
              ...word,
              meaning: null,
              selectedNum: null
            };
          }

          return { ...word }; // transcriptData의 복사본
        }),
      })),
    };
  };

  const navigate = useNavigate();
  const goBack = useBack(); // 뒤로가기 훅 호출
  
  const handleLater =() => {
    // 로그 기록
    logEvent("labeling_later", {
      hrId: id
    });
    goBack();
  }

  const handleSubmit = async() => {
    let delayTimer;

    try {
      setIsSubmitting(true);
      delayTimer = setTimeout(() => {
        setShowLoading(true);
      }, 400);
      const payload = createMappingPayload();
      // console.log('payload:', payload)
      await createConversationWord(id, payload)
      // console.log("저장 성공:", res)

      // 로그 기록
      logEvent("labeling_save", {
        hrId: id
      });

      navigate('/conversations')
    } catch (err) {
      console.error("저장 실패:", err);
      alert('오류가 발생했습니다. 다시 시도해주세요.');
    } finally {
      clearTimeout(delayTimer);
      setShowLoading(false);
      setIsSubmitting(false);
    }
  }

  const dropdown = useDropdown(); // 드롭다운 훅 호출
  const dropdownRef =useRef(null); //외부 클릭 감지를 위한 ref 
  useClickOutside(dropdownRef, () => dropdown.close()); // 외부 클릭 감지 훅 호출
  const modal = useModal();

   // 메뉴 클릭
  const handleMenuClick = () => {
    dropdown.open();
  }

  // 메뉴 > 제목 수정 선택
  const handleClickEdit = () => {
    dropdown.close();
    modal.open("edit")
  }

  // 메뉴 > 대화 삭제 선택
  const handleClickDelete = () => {
    dropdown.close();
    modal.open("delete");
  }

    // 제목 수정 모달 > 확인 선택
  const handleEditSubmit = (newTitle) => {
    const trimmed = newTitle.trim();
    const newValue = trimmed === '' ? '새로운 대화' : trimmed;  
    // setTitle(newValue);
    handleChangeTitle(newValue)
    modal.close();
  }

    // 삭제 모달 > 삭제 선택
  const handleDeleteSubmit = async() => {
    try {
        await deleteConversation(id)

        // 로그 기록
        logEvent("record_delete", {
          hrId: id
        })

        modal.close();
        // console.log(res)
        navigate('/conversations');  
      } catch (err) {
        console.error(err);
        alert('삭제 중 오류가 발생했습니다. 다시 시도해주세요.');
      }
    }

  // 모달 컴포넌트 object literal
  const modalComponents = {
    edit: () => <EditTitleModal initialTitle={title} onClose={modal.close} onConfirm={handleEditSubmit}/>,
    delete: () => <DeleteModal onClose={modal.close} onConfirm={handleDeleteSubmit} title="대화"/>
  }

  const CurrentModal = modal.modalType ? modalComponents[modal.modalType] : null;

  // 인스턴스 참조를 위한 ref 생성
  const audioPlayerRef = useRef(null);

  // 재생 및 타임스탬프 버튼 클릭 함수
  const handleSeek = (ms) => {
    // startTime(ms)와 Audio API(s)의 단위를 맞춰야함
    const seconds = ms / 1000; // ms를 s로 변환
    audioPlayerRef.current?.seekToTime(seconds);
  };

  return (
    <DefaultLayout
      headerType="sub"
      bgColor="gray"
      variant="action"
      onMenuClick={handleMenuClick}
      onBack={goBack}>
      {dropdown.isOpen && 
        <ConversationDetailDropdown
          ref={dropdownRef}
          onEdit={handleClickEdit}
          onDelete={handleClickDelete}/>}
        {transcriptData && 
          <>
            <RecordingMetaHeader
              recordedAt={formatUtcToLocalTime(transcriptData.recordedAt)}
              totalTime={formatDuration(transcriptData.totalTime)}
              title={title}
              onChange={handleChangeTitle}/>
            <TranscriptSection
              hrId={id}
              $isEditable={isEditable}
              data={transcriptData.segments} 
              newMeanings={newMeanings}
              setNewMeanings={setNewMeanings} 
              onMeaningChange={handleMeaningChange}
              onLater={handleLater}
              onSubmit={handleSubmit}
              onSeek={handleSeek}/>
            <AudioPlayerWrapper>
              <AudioPlayer 
                ref={audioPlayerRef}
                src={transcriptData.s3AudioUrl}/>
            </AudioPlayerWrapper>
          </>
        }   
      {CurrentModal && <CurrentModal/>}
      {showLoading && <LoadingOverlay/>}
    </DefaultLayout>
  )
}
export default ConversationTranscript
